import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCartSnapshot } from "../hooks/useCart";
import "../style/FlashSales.css";
import { routePaths } from "../constants/routes";
import { addToCart } from "../services/cartService";
import { FALLBACK_PRODUCT_IMAGE, fetchProducts } from "../services/commerceApi";

const formatPrice = (price) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(price);

export default function MegaFlashSale() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { cartIds, syncCartSnapshot } = useCartSnapshot();

  useEffect(() => {
    let isMounted = true;

    fetchProducts({ flashSale: true, limit: 40 })
      .then((data) => {
        if (isMounted) {
          setProducts(data);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error(error);
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const visibleProducts = products.filter((product) =>
    [product.name, product.category, product.description]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.trim().toLowerCase())
  );

  const handleAddToCart = (event, product) => {
    event.preventDefault();
    event.stopPropagation();
    if (cartIds.has(String(product.id))) {
      navigate(routePaths.cart);
      return;
    }
    addToCart(product);
    syncCartSnapshot();
    toast.success(`${product.name} added to cart.`);
  };

  return (
    <div className="flash-sale-page">
      <nav className="breadcrumb-nav">
        <NavLink to="/home" className="breadcrumb-item">Home</NavLink>
        <span className="breadcrumb-separator">{">"}</span>
        <NavLink to="/product" className="breadcrumb-item">Catalog</NavLink>
        <span className="breadcrumb-separator">{">"}</span>
        <span className="breadcrumb-item active">Mega Flash Sale</span>
      </nav>

      <header className="hero-header-box">
        <div className="hero-content">
          <h1>Mega Flash Sale</h1>
          <p>Discover database-backed deals and open each product in a browse-only catalog flow.</p>
        </div>
      </header>

      <section className="flash-toolbar">
        <div className="flash-toolbar-copy">
          <strong>Live sale catalog</strong>
          <span>Search featured deals and open their details without any checkout flow.</span>
        </div>
        <input
          type="search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search flash sale products"
          className="flash-search-input"
        />
      </section>

      <div className="grid-layout">
        {loading ? (
          <p className="loading-message">Loading flash sale products...</p>
        ) : (
          visibleProducts.map((product) => {
            const currentPrice = product.price * (1 - product.discount / 100);
            const stockPercent = (product.stock / Math.max(product.maxStock, 1)) * 100;

            return (
              <div key={product.id} className="product-card">
                <div className="image-container">
                  <span className="discount-badge">-{product.discount}%</span>
                  <img
                    src={product.listingImageUrl || product.url || FALLBACK_PRODUCT_IMAGE}
                    alt={product.name}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-card-body">
                  <span className="category-tag">{product.category}</span>
                  <p className="product-name-text">{product.name}</p>
                  <p className="flash-product-copy">{product.description}</p>
                  <div className="price-stack">
                    <span className="current-price">{formatPrice(currentPrice)}</span>
                    <span className="old-price">{formatPrice(product.price)}</span>
                  </div>

                  <div className="stock-progress-container">
                    <p className="stock-text">{product.stock} items left</p>
                    <div className="progress-bar-bg">
                      <div className="progress-bar-fill" style={{ width: `${stockPercent}%` }}></div>
                    </div>
                  </div>

                  <div className="flash-card-actions">
                    <button className="view-deal-btn" onClick={() => navigate(`/product-details/${product.id}`)}>
                      View Details
                    </button>
                    <button
                      className="action-btn add-to-cart-btn"
                      onClick={(event) => handleAddToCart(event, product)}
                    >
                      {cartIds.has(String(product.id)) ? "Go to Cart" : "Add to Cart"}
                    </button>
                    <button
                      className="action-btn add-to-cart-btn"
                      onClick={() => navigate(routePaths.productDetails(product.id))}
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
